import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { LocalStorageService } from '@tcc-mono/shared/ui/theming';
import { catchError, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { BaseService } from '@tcc-mono/shared/data-access';

export interface AppConfigResponse {
  meta: {
    schema_version: number;
    generated_at: string;
    cache_ttl_seconds?: number;
  };
  data: {
    transport_groups?: {
      blue_bont_types?: string[];
      defaults?: {
        max_age_days?: number;
      };
    };
    [k: string]: unknown;
  };
}

type CachedAppConfig = {
  etag?: string;
  fetched_at_ms: number;
  payload: AppConfigResponse;
};

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private readonly _http = inject(HttpClient);
  private readonly _appConfig = inject<IAppConfig>(APP_CONFIG);
  private readonly _storage = inject(LocalStorageService);

  private readonly _cacheKey = 'remote_app_config';

  private _baseService = new BaseService<unknown>(this._http, this._appConfig);

  public getAppConfig(forceRefresh: boolean = false): Observable<AppConfigResponse> {
    return of(null).pipe(
      switchMap(() => {
        const cached = this._getCached();
        const ttlMs = (cached?.payload?.meta?.cache_ttl_seconds ?? 300) * 1000;
        const isFresh = !!cached && Date.now() - cached.fetched_at_ms < ttlMs;

        if (cached && isFresh && !forceRefresh) {
          return of(cached.payload);
        }

        return this._fetchFromApi(cached?.etag).pipe(
          catchError((err: HttpErrorResponse) => {
            // Angular treats 304 as error because it's not 2xx
            if (err.status === 304 && cached?.payload) {
              // keep existing cache; treat as valid
              return of(cached.payload);
            }

            // If request fails but we have cached data, return it as best-effort
            if (cached?.payload) {
              return of(cached.payload);
            }

            return throwError(() => err);
          })
        );
      }),
      take(1)
    );
  }

  public getTransportGroupsConfig(): Observable<AppConfigResponse['data']['transport_groups']> {
    return this.getAppConfig().pipe(map(cfg => cfg?.data?.transport_groups));
  }

  private _fetchFromApi(etag?: string): Observable<AppConfigResponse> {
    const url = this._baseService.getUrl('api/app-config');
    const params = { app_code: this._appConfig.appCode };

    const headers = etag
      ? new HttpHeaders({ 'If-None-Match': etag })
      : new HttpHeaders();

    return this._http
      .get<AppConfigResponse>(url, { params, headers, observe: 'response' })
      .pipe(
        tap((res: HttpResponse<AppConfigResponse>) => {
          const payload = res.body as AppConfigResponse;
          if (!payload) return;

          const next: CachedAppConfig = {
            etag: res.headers.get('ETag') ?? undefined,
            fetched_at_ms: Date.now(),
            payload
          };

          this._storage.set({ key: this._cacheKey, value: next });
        }),
        map((res: HttpResponse<AppConfigResponse>) => res.body as AppConfigResponse)
      );
  }

  private _getCached(): CachedAppConfig | null {
    const cached = this._storage.get<CachedAppConfig>(this._cacheKey);
    return cached ?? null;
  }
}

